import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, Close } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';
import { formatDate as fmtDate } from '../../../utils/dateUtils';

const fmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PRIMARY = '#263078';
const PRIMARY_MID = '#3949ab';
const PRIMARY_LT = '#e8eaf6';
const th  = { padding: '6px 8px', textAlign: 'left',   fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
const td  = { padding: '5px 8px', textAlign: 'left',   fontSize: 12, whiteSpace: 'nowrap' };
const tdR = { padding: '5px 8px', textAlign: 'right',  fontSize: 12, whiteSpace: 'nowrap' };
const tdC = { padding: '5px 8px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' };

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const statusStyle = (status, daysDiff) => {
  const s = (status || '').toLowerCase();
  const d = parseInt(daysDiff) || 0;
  if (s === 'completed' || s === 'paid') return { bg: '#d1fae5', color: '#059669' };
  if (s.includes('partial') && d > 0)   return { bg: '#fef3c7', color: '#b45309' };
  if (s.includes('partial'))             return { bg: '#dbeafe', color: '#1d4ed8' };
  if (d > 0)                             return { bg: '#fecaca', color: '#dc2626' };
  return { bg: '#f3f4f6', color: '#6b7280' };
};

const fmt2Date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const payModeColor = (mode) => {
  const m = (mode || '').toLowerCase();
  if (m.includes('cash'))   return { bg: '#d1fae5', color: '#059669' };
  if (m.includes('cheque') || m.includes('check')) return { bg: '#dbeafe', color: '#1d4ed8' };
  if (m.includes('upi') || m.includes('online'))   return { bg: '#fef3c7', color: '#d97706' };
  if (m.includes('neft') || m.includes('rtgs'))    return { bg: '#ede9fe', color: '#7c3aed' };
  return { bg: '#f3f4f6', color: '#6b7280' };
};

const CollectionReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const [data, setData]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [fetched, setFetched]       = useState(false);
  const [search, setSearch]         = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData(
        'FinancialStatementsReportApi',
        `/CollectionReport/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setData([]);
      setFetched(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.CustomerName || '').toLowerCase().includes(q) ||
      (r.BillNo       || '').toLowerCase().includes(q) ||
      (r.ReceiptNo    || '').toLowerCase().includes(q) ||
      (r.Status       || '').toLowerCase().includes(q) ||
      (r.PaymentMode  || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const totalAmount   = filtered.reduce((s, r) => s + (parseFloat(r.TotalAmount)         || 0), 0);
  const totalReceived = filtered.reduce((s, r) => s + (parseFloat(r.TotalReceivedAmount) || 0), 0);
  const totalBalance  = filtered.reduce((s, r) => s + (parseFloat(r.BalanceAmount)       || 0), 0);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'CollectionReport', 0);
    if (!filtered.length) return;
    const cols = ['#', 'Customer', 'Bill No', 'Bill Date', 'Due Date', 'Total Amt', 'Received', 'Balance', 'Days', 'Status', 'Receipt No', 'Receipt Date', 'Received Amt', 'Pay Mode', 'Cheque No', 'Cheque Date'];
    const rows = filtered.map((r, i) => [
      i + 1, r.CustomerName, r.BillNo, fmtDate(r.BillDate), fmtDate(r.DueDate),
      parseFloat(r.TotalAmount) || 0, parseFloat(r.TotalReceivedAmount) || 0,
      parseFloat(r.BalanceAmount) || 0, parseInt(r.DaysDiff) || 0, r.Status || '',
      r.ReceiptNo, fmtDate(r.ReceiptDate), parseFloat(r.ReceivedAmount) || 0,
      r.PaymentMode || '', r.ChequeNumber || '', fmtDate(r.ChequeDate),
    ]);

      await exportStyledExcel({
          reportTitle: 'COLLECTION REPORT',
          sheetName: 'Collection Report',
          fileName: `CollectionReport_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          headers: cols,
          rows,
      });
  };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 8, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading collection report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '4px 10px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #1a237e 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Collection Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          {searchOpen ? (
            <>
              <TextField autoFocus size="small" placeholder="Search customer, bill..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
                  endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}><Close sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                }}
                sx={{ flexShrink: 0, width: 200, '& .MuiOutlinedInput-root': { height: 29, fontSize: 12, borderRadius: '8px', color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' } }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } }}
              />
              <Tooltip title="Close" arrow>
                <IconButton size="small" onClick={() => { setSearchOpen(false); setSearch(''); }} sx={iconBtn}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Search" arrow>
              <IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}>
                <Search sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate}   onChange={e => setToDate(e.target.value)}   style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 110px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1200 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={th}>#</th>
                <th style={th}>Customer</th>
                <th style={th}>Bill No</th>
                <th style={th}>Bill Date</th>
                <th style={{ ...th, textAlign: 'center', minWidth: 110 }}>Due Date</th>
                <th style={{ ...th, textAlign: 'right' }}>Total Amt</th>
                <th style={{ ...th, textAlign: 'right' }}>Received</th>
                <th style={{ ...th, textAlign: 'right' }}>Balance</th>
                <th style={{ ...th, textAlign: 'center' }}>Days</th>
                <th style={{ ...th, textAlign: 'center', minWidth: 120 }}>Status</th>
                <th style={th}>Receipt No</th>
                <th style={th}>Receipt Date</th>
                <th style={{ ...th, textAlign: 'right' }}>Rcvd Amt</th>
                <th style={{ ...th, textAlign: 'center' }}>Pay Mode</th>
                <th style={th}>Cheque No</th>
                <th style={th}>Cheque Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={16} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    No records found
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => {
                  const ss      = statusStyle(r.Status, r.DaysDiff);
                  const pc      = payModeColor(r.PaymentMode);
                  const days    = parseInt(r.DaysDiff) || 0;
                  const isPartial = (r.Status || '').toLowerCase().includes('partial');
                  const dueBg   = days > 0 ? (isPartial ? '#fef3c7' : '#fecaca') : days === 0 ? '#fef3c7' : '#d1fae5';
                  const dueClr  = days > 0 ? (isPartial ? '#b45309' : '#dc2626') : days === 0 ? '#d97706' : '#059669';
                  return (
                    <tr key={r.ReceiptId || i} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9' }}>
                      <td style={td}>{i + 1}</td>
                      <td style={{ ...td, fontWeight: 600, color: '#1e293b', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.CustomerName}</td>
                      <td style={{ ...td, color: PRIMARY, fontWeight: 600 }}>{r.BillNo}</td>
                      <td style={td}>{fmtDate(r.BillDate)}</td>
                      <td style={tdC}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: dueBg, color: dueClr, whiteSpace: 'nowrap', display: 'inline-block' }}>
                          {r.DueDate}
                        </span>
                      </td>
                      <td style={tdR}>{fmt(r.TotalAmount)}</td>
                      <td style={{ ...tdR, color: '#059669', fontWeight: 600 }}>{fmt(r.TotalReceivedAmount)}</td>
                      <td style={{ ...tdR, color: (parseFloat(r.BalanceAmount) || 0) > 0 ? '#dc2626' : '#374151', fontWeight: 600 }}>{fmt(r.BalanceAmount)}</td>
                      <td style={tdC}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: days > 0 ? (isPartial ? '#b45309' : '#dc2626') : days < 0 ? '#059669' : '#d97706' }}>
                          {days > 0 ? `+${days}` : days}
                        </span>
                      </td>
                      <td style={tdC}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: ss.bg, color: ss.color, whiteSpace: 'nowrap', display: 'inline-block' }}>
                          {r.Status || '-'}
                        </span>
                      </td>
                      <td style={{ ...td, color: PRIMARY, fontWeight: 600 }}>{r.ReceiptNo}</td>
                      <td style={td}>{fmtDate(r.ReceiptDate)}</td>
                      <td style={{ ...tdR, color: '#059669', fontWeight: 600 }}>{fmt(r.ReceivedAmount)}</td>
                      <td style={tdC}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: pc.bg, color: pc.color }}>
                          {r.PaymentMode || '-'}
                        </span>
                      </td>
                      <td style={{ ...td, color: '#64748b', fontSize: 11 }}>{r.ChequeNumber || '-'}</td>
                      <td style={{ ...td, color: '#64748b', fontSize: 11 }}>{fmtDate(r.ChequeDate)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700 }}>
                  <td colSpan={5} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                  <td style={tdR}>{fmt(totalAmount)}</td>
                  <td style={tdR}>{fmt(totalReceived)}</td>
                  <td style={tdR}>{fmt(totalBalance)}</td>
                  <td colSpan={8} style={td}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default CollectionReport;




